import React, { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Mail, Send, Loader2, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogClose,
} from './ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function EmailShareDialog({
  onGeneratePdf,
  defaultSubject = '',
  defaultFilename = 'document.pdf',
  triggerLabel = 'Email',
  triggerVariant = 'outline',
  triggerSize = 'sm',
  triggerClassName = '',
  triggerIcon,
}) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState(defaultSubject);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleOpen = (isOpen) => {
    setOpen(isOpen);
    if (isOpen) {
      setSent(false);
      setSubject(defaultSubject);
    }
  };

  const handleSend = async () => {
    if (!email.trim()) { toast.error('Enter a recipient email'); return; }
    if (!subject.trim()) { toast.error('Enter a subject'); return; }

    setSending(true);
    try {
      const pdfBase64 = await onGeneratePdf();
      if (!pdfBase64) { toast.error('Failed to generate PDF'); setSending(false); return; }

      await axios.post(`${API_URL}/api/share/email`, {
        recipient_email: email.trim(),
        subject: subject.trim(),
        message: message.trim(),
        pdf_base64: pdfBase64,
        filename: defaultFilename,
      }, { withCredentials: true });

      setSent(true);
      toast.success(`Sent to ${email.trim()}`);
      setTimeout(() => { setOpen(false); setEmail(''); setMessage(''); setSent(false); }, 1500);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to send email';
      toast.error(detail);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogTrigger asChild>
        <Button variant={triggerVariant} size={triggerSize} className={triggerClassName} data-testid="email-share-trigger">
          {triggerIcon || <Mail className="w-3.5 h-3.5 mr-1.5" />}
          {triggerLabel}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid="email-share-dialog">
        <DialogHeader>
          <DialogTitle className="text-base font-semibold text-[#0F2040] flex items-center gap-2">
            <Mail className="w-4 h-4 text-[#4ABFCF]" /> Share via Email
          </DialogTitle>
        </DialogHeader>

        {sent ? (
          <div className="flex flex-col items-center justify-center py-8" data-testid="email-sent-success">
            <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mb-3">
              <Check className="w-6 h-6 text-emerald-600" />
            </div>
            <p className="text-sm font-medium text-[#0F2040]">Email sent successfully</p>
            <p className="text-xs text-slate-400 mt-1">{email}</p>
          </div>
        ) : (
          <div className="space-y-4 pt-2">
            <div>
              <Label className="text-xs font-medium text-slate-600">Recipient Email</Label>
              <Input type="email" value={email} onChange={e => setEmail(e.target.value)}
                placeholder="stakeholder@company.com" className="mt-1 h-9 text-sm" data-testid="email-share-recipient" />
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-600">Subject</Label>
              <Input value={subject} onChange={e => setSubject(e.target.value)}
                placeholder="Document from Blueprint Studio" className="mt-1 h-9 text-sm" data-testid="email-share-subject" />
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-600">Message <span className="text-slate-400 font-normal">(optional)</span></Label>
              <textarea value={message} onChange={e => setMessage(e.target.value)}
                placeholder="Add a note for the recipient..."
                rows={3} className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                data-testid="email-share-message" />
            </div>
            <div className="flex items-center justify-between pt-1">
              <p className="text-[10px] text-slate-400">PDF will be attached automatically</p>
              <div className="flex gap-2">
                <DialogClose asChild>
                  <Button variant="ghost" size="sm" className="text-xs" data-testid="email-share-cancel">Cancel</Button>
                </DialogClose>
                <Button size="sm" onClick={handleSend} disabled={sending || !email.trim()}
                  className="bg-[#0F2040] hover:bg-[#1A3055] text-white text-xs" data-testid="email-share-send">
                  {sending ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
                  {sending ? 'Sending...' : 'Send'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
